import { PROGRAM_TAG_BY_PATH } from "@/lib/booking"
import { SITE_CONFIG } from "@/lib/site-config"

// Meta Pixel (browser side) - the ads-measurement counterpart to PostHog.
// PostHog stays the product-analytics and error-tracking tool; this module
// exists ONLY so Meta ads can be optimized and
// attributed against the funnel that matters (/apply -> Lead -> submitted ->
// booked). See context/meta-ads.md for the full event map + owner setup.
//
// Posture:
// - Env-gated on NEXT_PUBLIC_META_PIXEL_ID: when unset, it means
//   initMetaPixel() is a no-op - no fbevents.js script, no cookies, nothing
//   sent anywhere.
// - Never on /admin: the owner's console browsing is not ad data. The pixel
//   neither initializes on an /admin entry nor fires on an SPA hop into it.
// - Dedup with the server: the browser fires Lead only with the exact event_id
//   returned by the persist-first API response. The server derives that id
//   from the immutable coaching_applications row id, so browser and
//   Conversions API copies always name the same occurrence. The other
//   conversions (the application submission, the booking) are server-only.
// - Analytics must never break the UI: every export is defensive.

const OFFERING_CONTENT_BY_PATH: Record<string, string> = Object.fromEntries(
  Object.entries(PROGRAM_TAG_BY_PATH).map(([path, tag]) => [path, tag]),
)

type Fbq = {
  (...args: unknown[]): void
  callMethod?: (...args: unknown[]) => void
  queue: unknown[][]
  push: Fbq
  loaded: boolean
  version: string
}

declare global {
  interface Window {
    fbq?: Fbq
    _fbq?: Fbq
  }
}

let initialized = false
let startApplicationSent = false
const sentConversionIds = new Set<string>()

function pixelId(): string {
  return process.env.NEXT_PUBLIC_META_PIXEL_ID?.trim() ?? ""
}

/** /admin (including the private-messaging routes under it) never feeds Meta. */
function isExcludedPath(pathname: string): boolean {
  return pathname === "/admin" || pathname.startsWith("/admin/")
}

/**
 * ONLY the real production site may write to the live ad dataset.
 *
 * There is one root .env for every environment, so NEXT_PUBLIC_META_PIXEL_ID is
 * set on localhost and on every Vercel preview exactly as it is in production,
 * and an env-gate alone therefore gates nothing. Without this host check the dev
 * server fired real PageView/ViewContent at the live dataset on every page an
 * agent or a headless verification run loaded. Measured over one day, 15,463 of
 * 15,489 events Meta received came from http://localhost/ - 99.8% of the
 * dataset was developer traffic, against a real site seeing ~60 sessions a day.
 *
 * A preview deployment is excluded for the same reason: it is not the product.
 */
const PRODUCTION_HOSTS: ReadonlySet<string> = new Set(
  (() => {
    try {
      const host = new URL(SITE_CONFIG.url).hostname
      const bare = host.startsWith("www.") ? host.slice(4) : host
      return [bare, `www.${bare}`]
    } catch {
      return ["coldapproach.io", "www.coldapproach.io"]
    }
  })(),
)

function isProductionHost(): boolean {
  return PRODUCTION_HOSTS.has(window.location.hostname)
}

function fbq(...args: unknown[]): void {
  try {
    if (initialized && window.fbq) window.fbq(...args)
  } catch {
    // Swallow - ad measurement must never surface to the visitor.
  }
}

/** Standard fbevents.js bootstrap (the official snippet, written as TS
 * instead of an injected inline script). Queues calls until the script loads.
 *
 * The stub installs immediately so every early event (PageView, ViewContent,
 * the funnel events) queues in order, but the actual fbevents.js download is
 * DEFERRED to idle or the visitor's first interaction (perf, 2026-07-23):
 * Meta's script was ~700ms of startup work on the mobile money paths, and the
 * stub replays the whole queue when it arrives, so nothing is lost - plus the
 * server Conversions API twin (meta-capi.ts) covers the conversion events
 * regardless of when (or whether) the browser pixel loads. */
function loadFbq(id: string): void {
  if (window.fbq) return
  const stub = function (...args: unknown[]) {
    if (stub.callMethod) stub.callMethod(...args)
    else stub.queue.push(args)
  } as Fbq
  stub.queue = []
  stub.push = stub
  stub.loaded = true
  stub.version = "2.0"
  window.fbq = stub
  window._fbq = stub
  window.fbq("init", id)

  let injected = false
  const inject = () => {
    if (injected) return
    injected = true
    for (const event of INTERACTION_EVENTS) {
      window.removeEventListener(event, inject)
    }
    const script = document.createElement("script")
    script.async = true
    script.src = "https://connect.facebook.net/en_US/fbevents.js"
    document.head.appendChild(script)
  }
  for (const event of INTERACTION_EVENTS) {
    window.addEventListener(event, inject, { once: true, passive: true })
  }
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(() => inject(), { timeout: 4000 })
  } else {
    setTimeout(inject, 3000)
  }
}

const INTERACTION_EVENTS = ["pointerdown", "keydown", "scroll", "touchstart"] as const

function onRouteChange(pathname: string): void {
  if (isExcludedPath(pathname)) return
  fbq("track", "PageView")
  const content = OFFERING_CONTENT_BY_PATH[pathname]
  if (content) {
    fbq("track", "ViewContent", { content_name: content, content_category: "offering" })
  }
}

/**
 * Initialize the pixel + fire the landing PageView (and ViewContent when the
 * entry page is an offering page). Called once from instrumentation-client.ts.
 * posthog-js listens to history internally; fbevents.js does not, so SPA
 * navigations are covered by patching pushState/replaceState + popstate here.
 */
export function initMetaPixel(): void {
  if (typeof window === "undefined" || initialized) return
  const id = pixelId()
  if (!id) return
  if (!isProductionHost()) return
  if (isExcludedPath(window.location.pathname)) return
  try {
    loadFbq(id)
    initialized = true
    onRouteChange(window.location.pathname)

    const patch = (method: "pushState" | "replaceState") => {
      const original = history[method].bind(history)
      history[method] = (...args: Parameters<History["pushState"]>) => {
        const before = window.location.pathname
        original(...args)
        const after = window.location.pathname
        if (after !== before) onRouteChange(after)
      }
    }
    patch("pushState")
    patch("replaceState")
    window.addEventListener("popstate", () => onRouteChange(window.location.pathname))
  } catch {
    // A failed pixel load costs measurement, never the page.
  }
}

/** Fire a browser event with the opaque id computed and returned by the server.
 * A missing or malformed id means this browser event is not eligible. */
function trackDeduped(
  eventName: "Lead",
  candidate: string | number | boolean | null | undefined,
): void {
  if (!initialized || typeof candidate !== "string") return
  const eventID = candidate.trim()
  if (!/^[a-f0-9]{64}$/.test(eventID)) return
  const sentKey = `${eventName}:${eventID}`
  if (sentConversionIds.has(sentKey)) return
  sentConversionIds.add(sentKey)
  fbq("track", eventName, {}, { eventID })
}

/**
 * The analytics-event -> pixel-event mapping, called from track() in
 * web/lib/analytics.ts so the pixel needs zero changes at any call site.
 * Event names are compared as literals (not AnalyticsEvent imports) to avoid
 * a module cycle with analytics.ts; the taxonomy source of truth stays there.
 *
 * - apply_step_viewed (step 0)  -> StartApplication (custom; browser-only)
 * - lead_captured               -> Lead (deduped with the /apply API route).
 *   The event has more than one door; only /apply's carries the server-issued
 *   `meta_event_id`, so the newsletter bubble's capture never reaches the
 *   pixel. The pre-ruling name `apply_lead_captured` is still accepted for a
 *   browser running a bundle from before the rename.
 * - SubmitApplication and Schedule are SERVER-ONLY. Submission IS booking
 *   confirmation (his ruling: "Answer completion is progress. Booking
 *   confirmation is submission"), and the client fires no event at its final
 *   POST any more, so there is no browser half to pair with the /apply route's
 *   CAPI SubmitApplication. /what-happens-next has no email in browser
 *   context either, so a browser Schedule could never share the Cal webhook's
 *   dedup id - firing either from here would double count.
 */
export function forwardAnalyticsEventToMetaPixel(
  event: string,
  props?: Record<string, string | number | boolean | null | undefined>,
): void {
  if (!initialized) return
  if (event === "apply_step_viewed" && props?.step_index === 0) {
    if (startApplicationSent) return
    startApplicationSent = true
    fbq("trackCustom", "StartApplication")
    return
  }
  if (event === "lead_captured" || event === "apply_lead_captured") {
    trackDeduped("Lead", props?.meta_event_id)
  }
}
